import {usePrevious} from '@wandb/weave/common/state/hooks';
import {difference} from '@wandb/weave/common/util/data';
import _ from 'lodash';
import {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useInView} from 'react-intersection-observer';
import {
  ReactReduxContext,
  shallowEqual,
  useDispatch as useReduxDispatch,
  useSelector as useReduxSelector,
} from 'react-redux';
import {type Selector} from 'reselect';

import {envIsDev} from '../config';
import {type StoreType} from '../setup';
import type {Dispatch, RootAction, RootState} from '../types/redux';
import {useApolloClient as useApolloApolloClient} from './graphql/apolloWithAbort';
import type * as Types from './types';

export * from '@wandb/weave/common/state/hooks';

/**
 * If you need to access the redux store directly for whatever reason,
 * this is the recommended way to do so rather than directly importing
 * `store` from '../setup.ts'
 *
 * On hot reload, `setup.ts` sometimes re-runs, which creates a NEW
 * `store` with empty/default state. This may cause issues in the app
 * during development because the data you need is suddenly wiped.
 * However, `ReactReduxContext` will generally continue using the OLD
 * store from before the hot reload, so it still has all the previous
 * state. This is why all your components that `useSelector` to
 * retrieve redux state can work without issues on hot reload.
 */
export const useReduxStore = (): StoreType => {
  return useContext(ReactReduxContext).store;
};

/**
 * Hook for creating memoized selectors from selector factories.
 * Use this when you have a selector that depends on props or other arguments.
 *
 * The selector is recreated whenever the passed-in args change.
 *
 * @example
 * // Selector factory
 * const makeUserSelector = (userId: string) =>
 *   createSelector(selectUsers, users => users[userId]);
 *
 * // In component
 * const user = useMemoizedSelector(makeUserSelector, props.userId);
 */
export const useMemoizedSelector = <Args extends any[], R>(
  selectorFactory: (...args: Args) => Selector<RootState, R>,
  ...args: Args
) => {
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  const selector = useMemo(() => selectorFactory(...args), args);
  return useReduxSelector(selector, shallowEqual);
};

export const useDispatch = () => {
  return useReduxDispatch<Dispatch>();
};

export function useReduxAction<ArgType extends any[]>(
  fn: (...fnArgs: ArgType) => RootAction
) {
  const dispatch = useDispatch();
  return useCallback(
    (...args: ArgType) => dispatch(fn(...args)),
    [dispatch, fn]
  );
}

export function useChangedFromPrevious<T>(value: T): boolean {
  const prevValue = usePrevious(value);
  return value !== prevValue;
}

export function useChangedFromPreviousDeep<T>(value: T): boolean {
  const prevValue = usePrevious(value);
  return _.isEqual(value, prevValue);
}

type UseSelector = <TSelected>(
  selector: (state: RootState) => TSelected,
  equalityFn?: (left: TSelected, right: TSelected) => boolean,
  debugStr?: string
) => TSelected;

const useSelectorInner: UseSelector = (selector, equalityFn, debugStr) =>
  useReduxSelector(selector, equalityFn);

const useSelectorInnerWithDebug: UseSelector = (
  selector,
  equalityFn = (a, b) => a === b,
  debugStr
) => {
  const result = useSelectorInner(selector, equalityFn);

  const prevResult = usePrevious(result);
  if (debugStr && !equalityFn(prevResult!, result)) {
    console.group(`[useSelector]: ${debugStr} returned different result`);
    console.log('prev', prevResult);
    console.log('next', result);
    console.log('diff', difference(prevResult, result));
    console.groupEnd();
  }

  return result;
};

export const useSelector = envIsDev
  ? useSelectorInnerWithDebug
  : useSelectorInner;

// Returns true when domRef becomes onScreen for the first time, and stays
// true until value changes.
export function useWhenOnScreenAfterNewValue(value: any) {
  const {ref, inView} = useInView();
  const prevValue = usePrevious(value);
  // TODO: it would be nice to be able to init ready = true
  // but onScreen always starts false currently
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (inView) {
      if (!ready) {
        setReady(true);
      }
      return;
    }
    if (value !== prevValue && !inView) {
      setReady(false);
    }
  }, [inView, ready, prevValue, value]);
  return [ref, ready] as const;
}

export function useHasBeenOnScreen() {
  const [hasBeenOnScreen, setHasBeenOnScreen] = useState(false);
  const {ref, inView} = useInView();
  useEffect(() => {
    if (inView) {
      setHasBeenOnScreen(true);
    }
  }, [inView]);
  return {ref, hasBeenOnScreen} as const;
}

export const useApolloClient: () => Types.ApolloClient = () => {
  return useApolloApolloClient() as Types.ApolloClient;
};

// Same as memo, but value is delayed by a tick. This can be used
// to schedule expensive calculations for after the UI paints in response
// to a user action.
export const useDeferredMemo = <T>(fn: () => T, changeList: unknown[]) => {
  const [state, setState] = useState(() => fn());
  useEffect(() => {
    setTimeout(() => setState(fn()), 0);
    // We want useDeferredMemo to behave like useMemo, in that you
    // can pass an anonymous (unmemoized) function as the first argument.
    // So we only rerun this hook when changelist changes, not when fn
    // changes.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, changeList);
  return state;
};
